
import { useState, useEffect } from "react";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Database } from "@/integrations/supabase/types";

type MarketingProject = Database['public']['Tables']['marketing_projects']['Row'];
type MarketingContent = Database['public']['Tables']['marketing_content']['Row'];

interface ProjectFormValues {
  name: string;
}

interface ContentFormValues {
  name: string;
  url: string;
}

export const useProductData = (user: User | null) => {
  const [projects, setProjects] = useState<MarketingProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<MarketingProject | null>(null);
  const [contents, setContents] = useState<MarketingContent[]>([]);

  // Fetch projects
  useEffect(() => {
    const fetchProjects = async () => {
      if (!user) return;

      const { data, error } = await supabase
        .from('marketing_projects')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        toast.error('Failed to fetch projects');
        return;
      }

      setProjects(data || []);
      // Select the first project by default if available
      if (data && data.length > 0) {
        setSelectedProject(data[0]);
      }
    };

    fetchProjects();
  }, [user]);

  // Fetch contents
  useEffect(() => {
    const fetchContents = async () => {
      if (!selectedProject) {
        setContents([]);
        return;
      }

      const { data, error } = await supabase
        .from('marketing_content')
        .select('*')
        .eq('project_id', selectedProject.project_id)
        .order('created_at', { ascending: false });

      if (error) {
        toast.error('Failed to fetch content');
        return;
      }

      setContents(data || []);
    };

    fetchContents();
  }, [selectedProject]);

  const handleCreateProject = async (values: ProjectFormValues) => {
    if (!user) return;

    const { data, error } = await supabase
      .from('marketing_projects')
      .insert([{
        name: values.name,
        user_id: user.id
      }])
      .select()
      .single();

    if (error) {
      toast.error('Failed to create project');
      return;
    }

    if (data) {
      setProjects([data, ...projects]);
    }
  };

  const handleCreateContent = async (values: ContentFormValues, file?: File) => {
    if (!user || !selectedProject) return;

    let finalUrl = values.url;

    if (file) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('No session found');
        }

        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch('https://lzmumzmpkebtzmmljcwl.supabase.co/functions/v1/upload-marketing-content', {
          method: 'POST',
          body: formData,
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });

        if (!response.ok) {
          throw new Error('Upload failed');
        }

        const { publicUrl } = await response.json();
        if (!publicUrl) {
          throw new Error('No URL returned from upload');
        }

        finalUrl = publicUrl;
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Failed to upload file');
        return;
      }
    }

    const { data, error } = await supabase
      .from('marketing_content')
      .insert([{
        name: values.name.trim(),
        content_url: finalUrl,
        type: file ? 'file' : 'link',
        user_id: user.id,
        project_id: selectedProject.project_id
      }])
      .select()
      .single();

    if (error) {
      toast.error('Failed to create content');
      return;
    }

    if (data) {
      setContents([data, ...contents]);
    }
  };

  const handleEditContent = async (content: MarketingContent, values: ContentFormValues, file?: File) => {
    if (!user) return;

    let finalUrl = values.url;

    if (file) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('No session found');
        }

        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch('https://lzmumzmpkebtzmmljcwl.supabase.co/functions/v1/upload-marketing-content', {
          method: 'POST',
          body: formData,
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });

        if (!response.ok) {
          throw new Error('Upload failed');
        }

        const { publicUrl } = await response.json();
        if (!publicUrl) {
          throw new Error('No URL returned from upload');
        }

        finalUrl = publicUrl;
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Failed to upload file');
        return;
      }
    }

    const { error } = await supabase
      .from('marketing_content')
      .update({ 
        name: values.name.trim(),
        content_url: finalUrl,
        type: file ? 'file' : 'link'
      })
      .eq('id', content.id);

    if (error) {
      toast.error('Failed to update content');
      return;
    }

    setContents(contents.map(c => 
      c.id === content.id ? { 
        ...c, 
        name: values.name.trim(),
        content_url: finalUrl,
        type: file ? 'file' : 'link'
      } : c
    ));
  };

  const handleDeleteProject = async (project_id: string) => {
    const { error } = await supabase
      .from('marketing_projects')
      .delete()
      .eq('project_id', project_id);

    if (error) {
      toast.error('Failed to delete project');
      return;
    }

    setProjects(projects.filter(p => p.project_id !== project_id));
    if (selectedProject?.project_id === project_id) {
      setSelectedProject(null);
    }
  };

  const handleDeleteContent = async (id: string) => {
    const { error } = await supabase
      .from('marketing_content')
      .delete()
      .eq('id', id);

    if (error) {
      toast.error('Failed to delete content');
      return;
    }

    setContents(contents.filter(c => c.id !== id));
  };

  const handleEditProject = async (project_id: string, newName: string) => {
    const { error } = await supabase
      .from('marketing_projects')
      .update({ name: newName })
      .eq('project_id', project_id);

    if (error) {
      toast.error('Failed to update project');
      return;
    }

    setProjects(projects.map(p => 
      p.project_id === project_id ? { ...p, name: newName } : p
    ));
  };

  return {
    projects,
    selectedProject,
    contents,
    setSelectedProject,
    handleCreateProject,
    handleCreateContent,
    handleEditContent,
    handleDeleteProject,
    handleDeleteContent,
    handleEditProject
  };
};
